import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { CustomerPaymentMethod } from 'src/features/paymentMethods/utils/types';
import { useDeleteCustomerPaymentMethod } from 'src/features/paymentMethods/api/useDeleteCustomerPaymentMethod';
import { LIST_CUSTOMER_PAYMENT_METHODS_RESOURCE } from 'src/features/paymentMethods/api/useListCustomerPaymentMethods';
import { paymentMethodsMessages } from '../../utils/messages';

interface DeleteCustomerPaymentMethodFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedCustomerPaymentMethod: CustomerPaymentMethod;
    children?: React.ReactNode;
}

function DeleteCustomerPaymentMethodFormModal<TData>({
                                              selectedCustomerPaymentMethod,
                                              children,
                                          }: DeleteCustomerPaymentMethodFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { mutate: deletePaymentMethod } = useDeleteCustomerPaymentMethod({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_CUSTOMER_PAYMENT_METHODS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(paymentMethodsMessages.deleteCustomerPaymentMethodSuccess),
                description: formatMessage(paymentMethodsMessages.deleteCustomerPaymentMethodSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...paymentMethodsMessages.deleteCustomerPaymentMethod },
                subTitle: {
                    ...paymentMethodsMessages.deleteCustomerPaymentMethodSubtitle
            },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...paymentMethodsMessages.confirmDeleteCustomerPaymentMethod},
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: paymentMethodsMessages.deleteCustomerPaymentMethod,
                callbackFn: () => {
                    return (
                        deletePaymentMethod({
                            customerPaymentMethodId: selectedCustomerPaymentMethod.id,
                        }),
                            setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteCustomerPaymentMethodFormModal;
